import {noop} from "lodash";
import React, {useEffect, useMemo, useState} from "react";
import BasicText, {BasicNumber} from "../../components/form-v2/basic-text";
import {BlueButton, RedButton} from "../../components/ui/Buttons";
import {RiskRule} from "./types";

type RiskRowProps = {
    index: number
    row: RiskRule
    prev: RiskRule | undefined
    next: RiskRule | undefined
    remove?: (index: number) => void
    update?: (index: number, row: RiskRule) => void
}

export function RiskRow({
                            index,
                            row,
                            prev,
                            next,
                            remove = noop,
                            update = noop,
                        }: RiskRowProps) {

    const minRisk = useMemo(() => {
        if (!prev?.maxRisk) return 1
        return prev.maxRisk + 1
    }, [prev?.maxRisk])

    const maxRisk = useMemo(() => {
        if (!next?.minRisk) return 9
        return next.minRisk - 1
    }, [next?.minRisk])

    const [max, setMax] = useState(row.maxRisk)

    const [times, setTimes] = useState<string[]>(row.times)
    const [arrivalTravel, setArrivalTravel] = useState(row.onArrivalTravel)
    const [arrivalAccommodation, setArrivalAccommodation] = useState(row.onArrivalAccommodation)
    const [departureTravel, setDepartureTravel] = useState(row.onDepartureTravel)
    const [departureAccommodation, setDepartureAccommodation] = useState(row.onDepartureAccommodation)
    const [timeGracePeriod, setTimeGracePeriod] = useState(row.timeGracePeriod)
    const [arrivalGracePeriod, setArrivalGracePeriod] = useState(row.arrivalGracePeriod)
    const [departureGracePeriod, setDepartureGracePeriod] = useState(row.departureGracePeriod)


    useEffect(() => {
        update(index, {
            minRisk: minRisk,
            maxRisk: max,
            times: times,
            onArrivalTravel: arrivalTravel,
            onArrivalAccommodation: arrivalAccommodation,
            onDepartureTravel: departureTravel,
            onDepartureAccommodation: departureAccommodation,
            timeGracePeriod: timeGracePeriod,
            arrivalGracePeriod: arrivalGracePeriod,
            departureGracePeriod: departureGracePeriod,
        })

    }, [index, minRisk, max, times, arrivalTravel, arrivalAccommodation, departureTravel, departureAccommodation, timeGracePeriod, arrivalGracePeriod, departureGracePeriod])

    const addTime = () => {
        if (times.length == 0 || times[times.length - 1] != "") {
            setTimes([...times, ""])

        }
    }

    const removeTime = () => {
        const newTimes = [...times]
        newTimes.splice(newTimes.length - 1)
        setTimes(newTimes)
    }

    const updateTime = (value: string, index: number) => {
        const newTimes = [...times]
        newTimes[index] = value
        setTimes(newTimes)
    }


    return (
        <div className={"py-3 grid grid-cols-6 w-full items-start"}>
            <div className={"px-4"}>
                <BasicNumber type={"number"} value={minRisk} min={minRisk} max={9} disabled={true}/>
            </div>

            <div className={"px-4"}>
                <BasicNumber type={"number"} value={max} min={minRisk} max={maxRisk} onChange={setMax}
                             disabled={minRisk == 9}/>
            </div>

            <div className={"px-4 text-center"}>
                <div className={"flex flex-col gap-4"}>
                    {times.map((t, i) => <BasicText value={t} key={i} type={"time"} center={true}
                                                    onChange={(t) => updateTime(t, i)}/>)}
                    {times.length != 0 && <RedButton text={"Remove Time"} onClick={removeTime}/>}
                    <div className={(times.length == 0 || times[times.length - 1] != "") ? "" : "opacity-50"}>
                        <BlueButton
                            text={"Add Time"} onClick={addTime}/></div>
                </div>

                {times.length != 0 &&
                    <div className={"pt-4"}>
                        <label className={"font-semibold"}>Grace Period</label>
                        <BasicNumber type={"number"} value={timeGracePeriod} min={15} max={240}
                                     onChange={setTimeGracePeriod}
                        />
                    </div>
                }
            </div>


            <div className={"px-4 text-center h-full flex flex-col py-3"}>
                <div className={"flex flex-col gap-2 mb-4"}>
                    <label className={"font-semibold"}>Travel</label>
                    <input className={"scale-[1.5]"} type={"checkbox"} checked={arrivalTravel}
                           onChange={(e) => setArrivalTravel(e.target.checked)}/>
                </div>
                <div className={"flex flex-col gap-2"}>

                    <label className={"font-semibold"}>Accommodation</label>
                    <input className={"scale-[1.5]"} type={"checkbox"} checked={arrivalAccommodation}
                           onChange={(e) => setArrivalAccommodation(e.target.checked)}/>
                </div>

                {(arrivalTravel || arrivalAccommodation) &&
                    <div className={"pt-4"}>
                        <label className={"font-semibold"}>Grace Period</label>
                        <BasicNumber type={"number"} value={arrivalGracePeriod} min={15} max={240}
                                     onChange={setArrivalGracePeriod}
                        />
                    </div>
                }
            </div>
            <div className={"px-4 text-center h-full flex flex-col pt-3"}>
                <div className={"flex flex-col gap-2 mb-4"}>
                    <label className={"font-semibold"}>Travel</label>
                    <input className={"scale-[1.5]"} type={"checkbox"} checked={departureTravel}
                           onChange={(e) => setDepartureTravel(e.target.checked)}/>
                </div>
                <div className={"flex flex-col gap-2"}>

                    <label className={"font-semibold"}>Accommodation</label>
                    <input className={"scale-[1.5]"} type={"checkbox"} checked={departureAccommodation}
                           onChange={(e) => setDepartureAccommodation(e.target.checked)}/>
                </div>


                {(departureTravel || departureAccommodation) &&
                    <div className={"pt-4"}>
                        <label className={"font-semibold"}>Grace Period</label>
                        <BasicNumber type={"number"} value={departureGracePeriod} min={15} max={240}
                                     onChange={setDepartureGracePeriod}
                        />
                    </div>
                }
            </div>

            <div className={"px-4"}>
                {!next && index != 0 && <RedButton text={"Remove"} onClick={() => remove(index)}/>}
            </div>
        </div>
    )

}

